/**
 * synced-swipers plugin
 *
 * Wires multiple swipers to advance together. One swiper in the group is
 * marked the "parent" (`data-parent-swiper="true"`); when its slide changes,
 * each follower (`data-parent-swiper="false"`) receives a `slideToLoop` to
 * the same realIndex.
 *
 * Markup contract — every member of a sync group lives on a `.swiper-section`
 * carrying:
 *
 *   data-synced="true"
 *   data-sync-id="<group-id>"      // any string, must match across the group
 *   data-parent-swiper="true"      // one per group
 *   data-parent-swiper="false"     // every follower (must be present)
 *
 * Optionally, a member may also set `data-direction="vertical"` to receive
 * vertical layout, allowTouchMove disabled, and watchSlidesProgress (used
 * for paired horizontal-card / vertical-title strip pairs).
 *
 * --- Layout contract ---
 *
 * Every parent activeIndex must be a valid activeIndex on every follower.
 * In practice this means **the parent and every follower carry the same
 * slide count** — that's how every prod sync group is wired today (e.g.
 * `v3-outfits-carousel.liquid` pairs N cards with N vertical titles). The
 * follower's `slidesPerView` should also let every index be reachable, which
 * is automatic for `slidesPerView: 1` (the typical follower shape) and for
 * vertical title strips.
 *
 * If a follower can't reach the requested index, Swiper silently clamps.
 * To make that misconfig surface fast, the plugin warns once per
 * (follower, syncId) pair when an unreachable index is requested. The
 * warning fires from `slideChange` (not init) because Swiper's `snapGrid`
 * stabilises after layout.
 *
 * Why `=== "false"` for followers (faithful port note):
 * The legacy initializer's filter required followers to explicitly carry
 * `data-parent-swiper="false"` rather than just "anything that isn't the
 * parent". A swiper that's part of the group but missing this attribute
 * silently won't sync. That's opinionated and a bit of a footgun, but
 * loosening it would change behavior for existing prod sections. Preserve
 * for now; revisit later.
 */

const handlersBySwiper = new WeakMap();
const reachWarnedFollowers = new WeakSet();

/**
 * Returns the maximum activeIndex the follower can actually reach.
 *
 * Swiper exposes `snapGrid`: an array of valid translate positions. The last
 * entry corresponds to the highest reachable activeIndex; anything past it
 * gets clamped silently.
 *
 * Returns `Infinity` (treat as "unbounded") if snapGrid isn't available, so
 * the warning never trips on instances that don't expose it.
 *
 * Exported for unit-test access; not part of the plugin's public surface
 * area for runtime callers.
 */
export function maxReachableIndex(swiper) {
  if (!swiper || !Array.isArray(swiper.snapGrid)) return Infinity;
  return Math.max(0, swiper.snapGrid.length - 1);
}

function getSyncId(section) {
  return section?.dataset?.syncId || null;
}

function isParent(section) {
  return section?.dataset?.parentSwiper === 'true';
}

function isFollower(section) {
  return section?.dataset?.parentSwiper === 'false';
}

function isVertical(section) {
  return section?.dataset?.direction === 'vertical';
}

export const syncedSwipers = {
  name: 'synced-swipers',

  matches(_el, section) {
    return section?.dataset?.synced === 'true' && !!getSyncId(section);
  },

  parseOptions(_el, section, options) {
    if (isVertical(section)) {
      options.direction = 'vertical';
      options.allowTouchMove = false;
      options.watchSlidesProgress = true;
    }
    return options;
  },

  afterInit(swiper, ctx) {
    const syncId = getSyncId(ctx.parentSwiperSection);
    if (!syncId) return;
    ctx.registry.registerForSync(syncId, swiper);

    // Initial-sync-on-join. The normal initial sync runs from the parent's
    // `afterAllInit` — fine when the whole group constructs in one pass, but
    // gate plugins (tab-system) lazy-create followers on tab activation, by
    // which point the parent's afterAllInit has long since fired and the
    // group looks "fully synced" from the parent's POV. Without this nudge,
    // a follower that joins while the parent is at index 3 would visually
    // start at index 0 and stay there until the next slideChange.
    //
    // We only fire this if (a) we're a follower, (b) we're not already
    // destroyed, and (c) the parent has ALREADY had its afterAllInit run —
    // detected by the presence of a registered slideChange handler in
    // `handlersBySwiper`. If the parent's afterAllInit hasn't run yet (i.e.,
    // we're in the same batch construction), we'd queue a redundant
    // slideToLoop that the parent's own initial-sync fires moments later
    // anyway. Letting afterAllInit handle the same-batch case keeps the
    // visual feel cleaner (one settle, not two).
    if (swiper.destroyed) return;
    if (!isFollower(ctx.parentSwiperSection)) return;
    const group = ctx.registry.getSyncGroup(syncId) || [];
    const parent = group.find((m) => {
      if (m === swiper || m.destroyed) return false;
      const memberSection = m.el?.closest?.('.swiper-section');
      return isParent(memberSection);
    });
    if (!parent) return;
    if (!handlersBySwiper.has(parent)) return; // parent's afterAllInit hasn't fired yet
    if (!parent.realIndex) return;
    try {
      // Speed=0 — this is a "catch up to current state" jump, not an
      // animated transition. Match the no-animation feel of regular init.
      swiper.slideToLoop(parent.realIndex, 0);
    } catch (err) {
      console.warn('synced-swipers: initial-sync-on-join failed', err);
    }
  },

  afterAllInit(swiper, ctx) {
    if (!isParent(ctx.parentSwiperSection)) return;

    const syncId = getSyncId(ctx.parentSwiperSection);
    if (!syncId) return;

    const syncFollowers = () => {
      const group = ctx.registry.getSyncGroup(syncId);
      if (!group || group.length < 2) return;

      const targetIndex = swiper.realIndex;

      for (const member of group) {
        if (member === swiper) continue;
        if (member.destroyed) continue;

        const memberSection = member.el.closest('.swiper-section');
        if (!isFollower(memberSection)) continue;

        // Diagnose unreachable indices once per follower. Swiper would
        // silently clamp slideToLoop, leaving the visual out of sync —
        // surfacing it as a warning makes misconfigs trivial to spot.
        const max = maxReachableIndex(member);
        if (targetIndex > max && !reachWarnedFollowers.has(member)) {
          reachWarnedFollowers.add(member);
          console.warn(
            `[synced-swipers] follower in group "${syncId}" can't reach ` +
            `index ${targetIndex} (max reachable: ${max}). The follower's ` +
            `layout (slidesPerView vs slide count) limits its activeIndex ` +
            `range; see library/swiper/Swiper-Architecture.md "Synced group layout contract".`,
            { follower: member, parent: swiper }
          );
        }

        try {
          member.slideToLoop(targetIndex, 300);
        } catch (err) {
          console.warn('synced-swipers: slideToLoop failed', err);
        }
      }
    };

    swiper.on('slideChange', syncFollowers);
    handlersBySwiper.set(swiper, syncFollowers);

    // Initial sync — parents may not be at index 0 on init (e.g. when
    // re-init after resize while user was on slide 2).
    syncFollowers();
  },

  onDestroy(swiper, ctx) {
    const syncId = getSyncId(ctx.parentSwiperSection);
    if (syncId) {
      ctx.registry.unregisterFromSync(syncId, swiper);
    }
    // Swiper.destroy() removes its own event listeners; no need to detach
    // `slideChange` manually. Just drop our reference.
    handlersBySwiper.delete(swiper);
  },
};

export default syncedSwipers;
